import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  Calendar, MapPin, Clock, Users, ArrowLeft, Share2, Copy, MessageCircle, Mail,
  CheckCircle2, Loader2, QrCode, Download, User, Eye, Video, Wifi, Globe
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const SITE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function formatDate(d) {
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(d));
  } catch { return d; }
}

function FormField({ field, value, onChange }) {
  if (field.key === "phone") {
    return (
      <div className="space-y-1">
        <label className="text-sm text-[#94A3B8] font-medium">
          {field.label} {field.required && <span className="text-[#EF4444]">*</span>}
        </label>
        <PhoneInput
          international
          defaultCountry="FR"
          value={value || ""}
          onChange={(val) => onChange(field.key, val || "")}
          className="phone-input-intl"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }
  if (field.key === "country") {
    return (
      <div className="space-y-1">
        <label className="text-sm text-[#94A3B8] font-medium">
          {field.label} {field.required && <span className="text-[#EF4444]">*</span>}
        </label>
        <select value={value || ""} onChange={e => onChange(field.key, e.target.value)}
          className="w-full h-10 px-3 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm"
          data-testid={`field-${field.key}`}>
          <option value="">Choisir un pays...</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="space-y-1">
        <label className="text-sm text-[#94A3B8] font-medium">
          {field.label} {field.required && <span className="text-[#EF4444]">*</span>}
        </label>
        <Textarea value={value || ""} onChange={e => onChange(field.key, e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white placeholder:text-[#64748b]"
          placeholder={field.label} data-testid={`field-${field.key}`} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className="space-y-1">
        <label className="text-sm text-[#94A3B8] font-medium">
          {field.label} {field.required && <span className="text-[#EF4444]">*</span>}
        </label>
        <select value={value || ""} onChange={e => onChange(field.key, e.target.value)}
          className="w-full h-10 px-3 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm"
          data-testid={`field-${field.key}`}>
          <option value="">Choisir...</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label className="text-sm text-[#94A3B8] font-medium">
        {field.label} {field.required && <span className="text-[#EF4444]">*</span>}
      </label>
      <Input type={field.type || "text"} value={value || ""} onChange={e => onChange(field.key, e.target.value)}
        className="bg-[#0B0F19] border-[#2A3441] text-white placeholder:text-[#64748b]"
        placeholder={field.label} data-testid={`field-${field.key}`} />
    </div>
  );
}

const COUNTRIES = [
  "France", "Belgique", "Suisse", "Canada", "RD Congo", "Congo-Brazzaville",
  "Cameroun", "Cote d'Ivoire", "Senegal", "Mali", "Guinee", "Gabon",
  "Togo", "Benin", "Burkina Faso", "Niger", "Tchad", "Centrafrique",
  "Rwanda", "Burundi", "Madagascar", "Haiti", "Maroc", "Tunisie", "Algerie",
  "Luxembourg", "Allemagne", "Royaume-Uni", "Pays-Bas", "Espagne", "Italie",
  "Portugal", "Etats-Unis", "Bresil", "Angola", "Mozambique", "Afrique du Sud",
  "Autre",
];

export default function PublicEventPage() {
  const { orgSlug, eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get("preview");
  const { t } = useI18n();
  usePageTitle(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const isPreview = !!previewToken || data?.is_preview;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = previewToken
          ? `${API}/public/event/${orgSlug}/${eventSlug}?preview=${previewToken}`
          : `${API}/public/event/${orgSlug}/${eventSlug}`;
        const res = await axios.get(url);
        setData(res.data);
        const init = {};
        (res.data.event.form_fields || []).forEach(f => { if (f.enabled) init[f.key] = ""; });
        setForm(init);
      } catch (e) {
        setError(e.response?.data?.detail || "Evenement introuvable");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgSlug, eventSlug, previewToken]);

  useEffect(() => {
    if (data?.event?.title) document.title = `${data.event.title} — Inscription`;
  }, [data]);

  const event = data?.event;
  const org = data?.organization;
  const publicUrl = `${SITE}/e/${orgSlug}/${eventSlug}`;

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const fields = event.form_fields.filter(f => f.enabled);
    for (const f of fields) {
      if (f.required && !form[f.key]?.trim()) {
        toast.error(`Le champ "${f.label}" est obligatoire`);
        return;
      }
    }
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      toast.error("Adresse e-mail invalide");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/event/${orgSlug}/${eventSlug}/register`, form);
      setRegistered(res.data);
      toast.success("Inscription confirmee !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = (type) => {
    const title = event?.title || "Evenement";
    const date = formatDate(event?.event_date);
    const loc = event?.location || "";
    const text = `Vous etes invite(e) a ${title}\n${date}${loc ? `\n${loc}` : ""}\n\nInscription : ${publicUrl}`;
    if (type === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    else if (type === "email") window.open(`mailto:?subject=${encodeURIComponent(`Invitation : ${title}`)}&body=${encodeURIComponent(text)}`, "_blank");
    else if (type === "copy") { navigator.clipboard.writeText(publicUrl); toast.success("Lien copie !"); }
    setShowShare(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#F59E0B] animate-spin" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center text-center p-6">
      <div><p className="text-white text-xl font-bold mb-2">Evenement introuvable</p><p className="text-[#94A3B8]">{error}</p>
        <Link to="/" className="text-[#0099CC] mt-4 inline-block hover:underline">Retour a l'accueil</Link></div>
    </div>
  );

  const isFull = event.max_capacity > 0 && event.total_registered >= event.max_capacity;
  const regStatus = event.registration_status || "open";
  const isClosed = regStatus === "closed" || event.status === "ended" || event.status === "cancelled";
  const canWaitlist = isFull && event.waitlist_enabled && !isClosed;
  const enabledFields = (event.form_fields || []).filter(f => f.enabled).sort((a, b) => (a.order || 0) - (b.order || 0));
  const primaryColor = org?.primary_color || "#F59E0B";
  const showStats = isPreview || event.show_public_stats;
  const statsConfig = event.show_public_stats_config || {};

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="public-event-page">
      {/* Preview Banner */}
      {isPreview && (
        <div className="sticky top-0 z-50 bg-[#F59E0B] text-black py-2.5 px-4 text-center font-bold text-sm flex items-center justify-center gap-2" data-testid="preview-banner">
          <Eye className="h-4 w-4" />
          MODE PREVISUALISATION — Cet evenement n'est pas encore publie
        </div>
      )}

      {/* Cover Image */}
      {event.cover_image_url && (
        <div className="w-full max-h-[50vh] overflow-hidden relative">
          <img src={event.cover_image_url} alt={event.title} className="w-full h-full object-cover" data-testid="event-cover-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 pb-16" style={{ marginTop: event.cover_image_url ? "-4rem" : "2rem" }}>
        {/* Event Info Card */}
        <div className="relative bg-[#151B2B] border border-[#2A3441] rounded-2xl overflow-hidden" data-testid="event-info-card">
          <div className="p-6 sm:p-8">
            {/* Org badge */}
            {org?.name && (
              <p className="text-xs uppercase tracking-wider text-[#64748b] mb-3 font-medium" data-testid="event-org-name">
                {org.name}
              </p>
            )}

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight" data-testid="event-title">{event.title}</h1>

            {/* Date, Time, Location */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 shrink-0" style={{ color: primaryColor }} />
                <span className="text-white font-medium">{formatDate(event.event_date)}</span>
              </div>
              {(event.start_time || event.end_time) && (
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 shrink-0" style={{ color: primaryColor }} />
                  <span className="text-white">{event.start_time || ""}{event.end_time ? ` — ${event.end_time}` : ""}</span>
                </div>
              )}
              {event.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 shrink-0" style={{ color: primaryColor }} />
                  <span className="text-white">{event.location}{event.city ? `, ${event.city}` : ""}</span>
                </div>
              )}
              {/* Event format badge */}
              {event.event_format && event.event_format !== "presentiel" && (
                <div className="flex items-center gap-3" data-testid="event-format-info">
                  {event.event_format === "hybride" ? (
                    <Globe className="h-5 w-5 shrink-0 text-[#8B5CF6]" />
                  ) : (
                    <Wifi className="h-5 w-5 shrink-0 text-[#3B82F6]" />
                  )}
                  <div>
                    <span className="text-white text-sm">{event.event_format === "hybride" ? "Evenement hybride" : "Evenement en ligne"}</span>
                    {event.meeting_config?.show_link_publicly && event.meeting_config?.meeting_url && (
                      <a href={event.meeting_config.meeting_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[#3B82F6] text-xs mt-0.5 hover:underline">
                        <Video className="h-3 w-3" /> Lien de la reunion
                      </a>
                    )}
                    {!event.meeting_config?.show_link_publicly && event.event_format !== "presentiel" && (
                      <p className="text-[#64748b] text-xs mt-0.5">Le lien de reunion sera fourni apres inscription</p>
                    )}
                  </div>
                </div>
              )}
              {/* Stats: visible in preview mode OR when organizer enabled show_public_stats */}
              {showStats && event.max_capacity > 0 && (
                <div className="flex items-center gap-3" data-testid="public-stats-display">
                  <Users className="h-5 w-5 shrink-0" style={{ color: primaryColor }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {(isPreview || statsConfig.inscrits) && (
                        <span className="text-white text-sm font-medium">{event.total_registered || 0} inscrit(s)</span>
                      )}
                      {(isPreview || statsConfig.places_dispo) && event.places_available != null && (
                        <span className="text-[#10B981] text-sm"> — {event.places_available} place(s) disponible(s)</span>
                      )}
                      {(isPreview || statsConfig.capacite) && (
                        <span className="text-[#64748b] text-sm">/ {event.max_capacity}</span>
                      )}
                    </div>
                    <div className="max-w-[200px] h-2 bg-[#1E293B] rounded-full overflow-hidden mt-1">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, ((event.total_registered || 0) / event.max_capacity) * 100)}%`, background: primaryColor }} />
                    </div>
                  </div>
                </div>
              )}
              {showStats && !event.max_capacity && (event.total_registered || 0) > 0 && (
                <div className="flex items-center gap-3" data-testid="public-stats-display">
                  <Users className="h-5 w-5 shrink-0" style={{ color: primaryColor }} />
                  <span className="text-white text-sm font-medium">{event.total_registered} participant(s) inscrit(s)</span>
                </div>
              )}
              {/* Event status badges */}
              {event.status === "cancelled" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center" data-testid="event-cancelled-badge">
                  <p className="text-red-400 font-bold text-sm">Evenement annule</p>
                </div>
              )}
              {event.status === "ended" && (
                <div className="bg-[#64748b]/10 border border-[#64748b]/30 rounded-lg p-3 text-center" data-testid="event-ended-badge">
                  <p className="text-[#94A3B8] font-bold text-sm">Evenement termine</p>
                </div>
              )}
              {/* Replay disponible */}
              {event.status === "ended" && event.recording_url && (
                <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl p-4" data-testid="replay-available-badge">
                  <div className="flex items-center gap-2 mb-2">
                    <Video className="h-5 w-5 text-[#8B5CF6]" />
                    <span className="text-[#8B5CF6] font-bold text-sm">Replay disponible</span>
                  </div>
                  <a href={event.recording_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:-translate-y-0.5"
                    style={{ background: primaryColor }}
                    data-testid="replay-link">
                    <Eye className="h-4 w-4" /> Voir le replay
                  </a>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div className="text-[#94A3B8] text-sm leading-relaxed whitespace-pre-line mb-6 border-t border-[#2A3441] pt-4" data-testid="event-description">
                {event.description}
              </div>
            )}

            {/* Share button */}
            <div className="relative mb-6">
              <Button variant="outline" size="sm" onClick={() => setShowShare(!showShare)}
                className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="share-btn">
                <Share2 className="h-4 w-4 mr-1.5" /> Partager
              </Button>
              {showShare && (
                <div className="absolute top-full left-0 mt-2 bg-[#1E293B] border border-[#2A3441] rounded-xl p-3 flex gap-2 z-10 shadow-xl animate-fade-in">
                  <Button size="sm" onClick={() => handleShare("whatsapp")} className="bg-[#25D366] hover:bg-[#20BD5A] text-white" data-testid="share-whatsapp">
                    <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                  </Button>
                  <Button size="sm" onClick={() => handleShare("email")} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="share-email">
                    <Mail className="h-4 w-4 mr-1" /> Email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleShare("copy")} className="border-[#2A3441] text-white" data-testid="share-copy">
                    <Copy className="h-4 w-4 mr-1" /> Copier
                  </Button>
                </div>
              )}
            </div>

            {/* ── REGISTRATION FORM ── */}
            {!registered ? (
              <>
                {isPreview ? (
                  <div className="border-t border-[#2A3441] pt-6 space-y-4">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <User className="h-5 w-5" style={{ color: primaryColor }} /> S'inscrire
                    </h2>
                    {enabledFields.map(f => (
                      <div key={f.key} className="space-y-1 opacity-60">
                        <label className="text-sm text-[#94A3B8] font-medium">
                          {f.label} {f.required && <span className="text-[#EF4444]">*</span>}
                        </label>
                        <Input disabled type={f.type || "text"} className="bg-[#0B0F19] border-[#2A3441] text-[#64748b]" placeholder={f.label} data-testid={`field-${f.key}`} />
                      </div>
                    ))}
                    <Button disabled className="w-full h-12 text-white font-bold rounded-xl text-base opacity-60" style={{ background: primaryColor }} data-testid="register-submit-btn">
                      S'inscrire
                    </Button>
                    <p className="text-[#F59E0B] text-xs text-center font-medium">
                      Apercu du formulaire — les inscriptions ne sont pas actives en mode previsualisation.
                    </p>
                  </div>
                ) : isClosed ? (
                  <div className={`rounded-xl p-5 text-center ${
                    event.status === "cancelled" ? "bg-red-500/10 border border-red-500/30" :
                    event.status === "ended" ? "bg-[#8B5CF6]/10 border border-[#8B5CF6]/30" :
                    "bg-[#64748b]/10 border border-[#64748b]/30"
                  }`} data-testid="registrations-closed">
                    {event.status === "cancelled" ? (
                      <>
                        <p className="text-red-400 font-bold text-lg">Evenement annule</p>
                        <p className="text-[#94A3B8] text-sm mt-2">Cet evenement a ete annule par l'organisateur.</p>
                      </>
                    ) : event.status === "ended" ? (
                      <>
                        <p className="text-[#8B5CF6] font-bold text-lg">Evenement termine</p>
                        <p className="text-[#94A3B8] text-sm mt-2">Cet evenement est termine. Merci a tous les participants !</p>
                      </>
                    ) : (
                      <>
                        <p className="text-white font-bold text-lg">Inscriptions fermees</p>
                        <p className="text-[#94A3B8] text-sm mt-2">Les inscriptions a cet evenement sont actuellement closes.</p>
                      </>
                    )}
                  </div>
                ) : canWaitlist ? (
                  <form onSubmit={handleSubmit} className="space-y-4 border-t border-[#2A3441] pt-6" data-testid="waitlist-form">
                    <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 text-center mb-4">
                      <p className="text-[#F59E0B] font-bold">Evenement complet</p>
                      <p className="text-[#94A3B8] text-sm mt-1">Vous pouvez rejoindre la liste d'attente.</p>
                    </div>
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <User className="h-5 w-5" style={{ color: "#F59E0B" }} /> Rejoindre la liste d'attente
                    </h2>
                    {enabledFields.map(f => (
                      <FormField key={f.key} field={f} value={form[f.key]} onChange={handleChange} />
                    ))}
                    <Button type="submit" disabled={submitting}
                      className="w-full h-12 text-black font-bold rounded-xl text-base bg-[#F59E0B] hover:bg-[#D97706]"
                      data-testid="register-submit-btn">
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                      {submitting ? "Envoi..." : "Rejoindre la liste d'attente"}
                    </Button>
                  </form>
                ) : isFull ? (
                  <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 text-center" data-testid="event-full">
                    <p className="text-[#EF4444] font-bold">Complet</p>
                    <p className="text-[#94A3B8] text-sm mt-1">Toutes les places ont ete reservees.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 border-t border-[#2A3441] pt-6" data-testid="registration-form">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <User className="h-5 w-5" style={{ color: primaryColor }} /> S'inscrire
                    </h2>
                    {enabledFields.map(f => (
                      <FormField key={f.key} field={f} value={form[f.key]} onChange={handleChange} />
                    ))}
                    <Button type="submit" disabled={submitting}
                      className="w-full h-12 text-white font-bold rounded-xl text-base"
                      style={{ background: primaryColor }}
                      data-testid="register-submit-btn">
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                      {submitting ? "Inscription en cours..." : "S'inscrire"}
                    </Button>
                    <p className="text-[#64748b] text-xs text-center">
                      En vous inscrivant, vous acceptez que vos informations soient transmises a l'organisateur.
                    </p>
                  </form>
                )}
              </>
            ) : (
              /* ── CONFIRMATION ── */
              <div className="border-t border-[#2A3441] pt-6 text-center space-y-4 animate-fade-in" data-testid="registration-confirmation">
                {registered.status === "waitlisted" ? (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-2 bg-[#F59E0B]/10">
                      <Clock className="h-8 w-8 text-[#F59E0B]" />
                    </div>
                    <h2 className="text-xl font-bold text-white">Liste d'attente</h2>
                    <p className="text-[#94A3B8]">
                      {registered.participant?.first_name}, votre demande a ete enregistree. Nous vous contacterons si une place se libere.
                    </p>
                    {registered.waitlist_position && (
                      <p className="text-[#F59E0B] text-sm font-mono">Position #{registered.waitlist_position}</p>
                    )}
                    <p className="text-sm text-[#64748b]">
                      Reference : <span className="text-white font-mono font-bold">{registered.reference}</span>
                    </p>
                    <p className="text-xs text-[#64748b]">Un email de confirmation vous a ete envoye.</p>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-2" style={{ background: `${primaryColor}15` }}>
                      <CheckCircle2 className="h-8 w-8" style={{ color: primaryColor }} />
                    </div>
                    <h2 className="text-xl font-bold text-white">Inscription confirmee !</h2>
                    <p className="text-[#94A3B8]">
                      {registered.participant?.first_name || form.first_name}, vous etes inscrit(e) a <strong className="text-white">{event.title}</strong>.
                    </p>
                    {(registered.qr_code || registered.qr_base64) && (
                      <div className="bg-white rounded-xl p-4 inline-block mx-auto" data-testid="qr-code-display">
                        <img src={`data:image/png;base64,${registered.qr_code || registered.qr_base64}`}
                          alt="QR Code" className="w-48 h-48 mx-auto" />
                      </div>
                    )}
                    <p className="text-sm text-[#64748b]">
                      Reference : <span className="text-white font-mono font-bold">{registered.reference}</span>
                    </p>
                    <p className="text-xs text-[#64748b]">Un email de confirmation avec votre QR Code vous a ete envoye.</p>
                    <p className="text-xs text-[#64748b]">Presentez ce QR Code a l'entree de l'evenement.</p>
                    {registered.badge_url && (
                      <a
                        href={registered.badge_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="badge-link"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all mt-2"
                        style={{ background: primaryColor }}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="4"/><path d="M7 7h4v4H7zM13 7h4v4h-4zM7 13h4v4H7z"/></svg>
                        Mon badge digital
                      </a>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[#64748b] text-xs mt-8">
          Propulse par <a href="/" className="text-[#0099CC] hover:underline">GLOTECH Event Center</a>
        </p>
      </div>
    </div>
  );
}
